import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ThemeToggle } from '../components/ThemeToggle';
import { CalendarDays, Plus, Check, X, Trash2, RefreshCw, Link2, Unlink } from 'lucide-react';
import axios from 'axios';
import { sessionUnits, unitsLabel } from '../lib/sessionUnits';
import { useConfirm } from '../contexts/ConfirmContext';
import { toast } from 'sonner';

import { localISODate } from '../lib/utils';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_META = {
  scheduled: { label: 'ZAPLANOWANA', className: 'text-[#C9A84C] bg-[#C9A84C]/10 border-[#C9A84C]/30' },
  completed: { label: 'ODBYTA', className: 'text-green-500 bg-green-500/10 border-green-500/30' },
  cancelled: { label: 'ANULOWANA', className: 'text-red-400 bg-red-500/10 border-red-500/30' },
  no_show: { label: 'NIEOBECNOŚĆ', className: 'text-[#A1A1A1] bg-[#2A2A2A]/40 border-[#2A2A2A]' },
};

const emptyForm = {
  client_id: '',
  package_id: '',
  date: localISODate(),
  start_time: '10:00',
  end_time: '11:00',
  location: '',
  notes: '',
};

export const CalendarPage = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [clientPackages, setClientPackages] = useState([]);
  const [googleStatus, setGoogleStatus] = useState({ connected: false, google_email: null, last_synced_at: null });
  const [syncing, setSyncing] = useState(false);
  const [connectMsg, setConnectMsg] = useState('');
  const [googleEvents, setGoogleEvents] = useState([]);

  useEffect(() => {
    fetchAll();
    const gc = searchParams.get('google_calendar');
    if (gc === 'connected') setConnectMsg('Połączono z Google Calendar.');
    else if (gc === 'noreftoken') setConnectMsg('Nie udało się uzyskać trwałego dostępu — odłącz dostęp aplikacji w ustawieniach konta Google i spróbuj ponownie.');
    else if (gc === 'error') setConnectMsg('Błąd połączenia z Google Calendar.');
  }, []);

  const fetchAll = async () => {
    try {
      const [clientsRes, sessionsRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/clients`, { withCredentials: true }),
        axios.get(`${API_URL}/api/sessions`, { withCredentials: true }),
        axios.get(`${API_URL}/api/google-calendar/status`, { withCredentials: true }),
      ]);
      setClients(clientsRes.data);
      setSessions(sessionsRes.data);
      setGoogleStatus(statusRes.data);
      if (statusRes.data.connected) fetchGoogleEvents();
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Next 30 days of Google Calendar events that aren't app sessions (their
  // title didn't match a client), shown read-only so nothing planned is hidden.
  const fetchGoogleEvents = async () => {
    const iso = (d) => d.toLocaleDateString('sv-SE');
    const from = new Date();
    const to = new Date(Date.now() + 30 * 864e5);
    try {
      const res = await axios.get(`${API_URL}/api/google-calendar/events?start=${iso(from)}&end=${iso(to)}`, { withCredentials: true });
      const nowLocal = new Date();
      setGoogleEvents(res.data.filter(e => new Date(e.end_time) >= nowLocal));
    } catch (error) {
      console.error('Error fetching Google events:', error);
    }
  };

  const handleClientChange = async (clientId) => {
    setForm(prev => ({ ...prev, client_id: clientId, package_id: '' }));
    if (!clientId) { setClientPackages([]); return; }
    try {
      const res = await axios.get(`${API_URL}/api/packages/client/${clientId}`, { withCredentials: true });
      setClientPackages(res.data.filter(p => p.status === 'active'));
    } catch (error) {
      setClientPackages([]);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.date || !form.start_time || !form.end_time) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/sessions`, {
        client_id: form.client_id,
        package_id: form.package_id || null,
        start_time: `${form.date}T${form.start_time}:00`,
        end_time: `${form.date}T${form.end_time}:00`,
        location: form.location || null,
        notes: form.notes || null,
      }, { withCredentials: true });
      setSessions([...sessions, res.data].sort((a, b) => a.start_time.localeCompare(b.start_time)));
      setDialogOpen(false);
      setForm(emptyForm);
      setClientPackages([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd zapisu sesji');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (session, status) => {
    setBusyId(session.id);
    try {
      const res = await axios.put(`${API_URL}/api/sessions/${session.id}`, { status }, { withCredentials: true });
      setSessions(sessions.map(s => s.id === session.id ? res.data : s));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd aktualizacji sesji');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (session) => {
    if (!await confirm(`Usunąć sesję z ${session.client_name}?`)) return;
    setBusyId(session.id);
    try {
      await axios.delete(`${API_URL}/api/sessions/${session.id}`, { withCredentials: true });
      setSessions(sessions.filter(s => s.id !== session.id));
    } catch (error) {
      toast.error('Błąd usuwania sesji');
    } finally {
      setBusyId(null);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/google-calendar/connect`, { withCredentials: true });
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Integracja z Google Calendar nie jest jeszcze skonfigurowana');
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!await confirm('Rozłączyć Google Calendar? Istniejące sesje zostaną w aplikacji, ale przestaną się synchronizować.')) return;
    try {
      await axios.post(`${API_URL}/api/google-calendar/disconnect`, {}, { withCredentials: true });
      setGoogleStatus({ connected: false, google_email: null, last_synced_at: null });
    } catch (error) {
      toast.error('Błąd rozłączania');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API_URL}/api/google-calendar/sync`, {}, { withCredentials: true });
      const { updated_count, imported_count } = res.data;
      const parts = [];
      if (imported_count) parts.push(`zaimportowano ${imported_count} ${imported_count === 1 ? 'nową sesję' : 'nowych sesji'} z Google Calendar`);
      if (updated_count) parts.push(`zaktualizowano ${updated_count} ${updated_count === 1 ? 'sesję' : 'sesji'}`);
      setConnectMsg(parts.length ? parts.join(', ') + '.' : 'Brak zmian do zsynchronizowania.');
      await fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd synchronizacji');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-white">Ładowanie...</div>
      </div>
    );
  }

  const now = new Date().toISOString();
  const upcoming = sessions.filter(s => s.status === 'scheduled' && s.end_time >= now);
  const past = sessions.filter(s => s.status !== 'scheduled' || s.end_time < now).reverse();

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="calendar-page">
      <nav className="border-b border-[#2A2A2A] px-4 sm:px-8 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <Logo size="md" />
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-[#A1A1A1] hover:text-white px-2 sm:px-4">
              Powrót
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-2">
            <CalendarDays className="w-7 h-7 text-[#C9A84C]" />
            <h1 className="display-text text-4xl text-white">Kalendarz</h1>
          </div>
          <p className="body-text text-[#A1A1A1] text-lg">Zaplanowane sesje treningowe</p>
        </div>

        {connectMsg && (
          <div className="mb-6 p-4 rounded-2xl bg-[#C9A84C]/10 border border-[#C9A84C]/30 text-sm text-[#C9A84C]">
            {connectMsg}
          </div>
        )}

        <div className="card-surface rounded-3xl p-6 mb-10 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            {googleStatus.connected ? <Link2 className="w-5 h-5 text-green-500" /> : <Unlink className="w-5 h-5 text-[#A1A1A1]" />}
            <div>
              <p className="text-white font-medium">
                {googleStatus.connected ? `Połączono z Google Calendar (${googleStatus.google_email})` : 'Google Calendar niepołączony'}
              </p>
              {googleStatus.connected && (
                <p className="text-xs text-[#A1A1A1]">
                  {googleStatus.last_synced_at ? `Ostatnia synchronizacja: ${new Date(googleStatus.last_synced_at).toLocaleString('pl-PL')}` : 'Jeszcze nie synchronizowano'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {googleStatus.connected ? (
              <>
                <Button onClick={handleSync} disabled={syncing} variant="ghost" className="text-[#C9A84C] hover:text-white border border-[#C9A84C]/40 rounded-full" data-testid="sync-google-button">
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} /> Synchronizuj teraz
                </Button>
                <Button onClick={handleDisconnectGoogle} variant="ghost" className="text-[#A1A1A1] hover:text-red-400 rounded-full">
                  Rozłącz
                </Button>
              </>
            ) : (
              <Button onClick={handleConnectGoogle} className="bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full" data-testid="connect-google-button">
                Połącz z Google Calendar
              </Button>
            )}
          </div>
        </div>

        {googleStatus.connected && (
          <p className="text-xs text-[#A1A1A1] -mt-7 mb-10">
            Możesz też dodać trening bezpośrednio w Google Calendar — wpisz w tytule imię i nazwisko klienta tak, jak w aplikacji, a potem kliknij „Synchronizuj teraz", żeby zaimportować go jako sesję.
          </p>
        )}

        <div className="flex items-center justify-between mb-6">
          <h2 className="heading-text text-2xl text-white">Nadchodzące sesje</h2>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setForm(emptyForm); setClientPackages([]); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full" data-testid="new-session-button">
                <Plus className="w-4 h-4 mr-2" /> Nowa sesja
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1E1E1E] border-[#2A2A2A] rounded-3xl max-w-md max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="heading-text text-2xl text-white">Nowa sesja</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-5 mt-2">
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">KLIENT</Label>
                  <Select value={form.client_id} onValueChange={handleClientChange}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white" data-testid="session-client-select">
                      <SelectValue placeholder="Wybierz klienta" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E1E1E] border-[#2A2A2A] text-white">
                      {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.client_id && (
                  <div>
                    <Label className="label-text text-[#A1A1A1] mb-2 block">PAKIET (OPCJONALNIE)</Label>
                    <Select value={form.package_id} onValueChange={(v) => setForm({ ...form, package_id: v })}>
                      <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white" data-testid="session-package-select">
                        <SelectValue placeholder={clientPackages.length ? 'Bez pakietu' : 'Brak aktywnych pakietów'} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E1E1E] border-[#2A2A2A] text-white">
                        {clientPackages.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.package_label}{p.sessions_total ? ` (${p.sessions_used}/${p.sessions_total})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {form.package_id && (
                      <p className="text-xs text-[#A1A1A1] mt-2">Sesja zostanie zużyta z pakietu dopiero gdy oznaczysz trening jako odbyty.</p>
                    )}
                  </div>
                )}
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">DATA</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="label-text text-[#A1A1A1] mb-2 block">OD</Label>
                    <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white" required />
                  </div>
                  <div>
                    <Label className="label-text text-[#A1A1A1] mb-2 block">DO</Label>
                    <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white" required />
                  </div>
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">MIEJSCE (OPCJONALNIE)</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="np. Siłownia, Online" className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white" />
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">NOTATKA (OPCJONALNIE)</Label>
                  <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white" />
                </div>
                <Button type="submit" disabled={saving} className="w-full bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full h-12" data-testid="save-session-button">
                  {saving ? 'Zapisywanie...' : 'Zapisz sesję'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {upcoming.length === 0 ? (
          <div className="card-surface rounded-3xl p-12 text-center mb-12">
            <CalendarDays className="w-12 h-12 text-[#A1A1A1] mx-auto mb-4" />
            <p className="body-text text-[#A1A1A1]">Brak zaplanowanych sesji.</p>
          </div>
        ) : (
          <div className="space-y-3 mb-12">
            {upcoming.map(session => (
              <SessionRow key={session.id} session={session} busy={busyId === session.id} onComplete={() => updateStatus(session, 'completed')} onNoShow={() => updateStatus(session, 'no_show')} onCancel={() => updateStatus(session, 'cancelled')} onDelete={() => handleDelete(session)} />
            ))}
          </div>
        )}

        {googleEvents.length > 0 && (
          <div className="mb-12" data-testid="google-events">
            <h2 className="heading-text text-2xl text-white mb-2">Z Google Calendar</h2>
            <p className="text-xs text-[#A1A1A1] mb-6">
              Najbliższe 30 dni. Te wydarzenia nie są przypisane do klienta w aplikacji — żeby rozliczały pakiet, wpisz w tytule imię i nazwisko klienta i kliknij „Synchronizuj teraz".
            </p>
            <div className="space-y-3">
              {googleEvents.map(ev => (
                <div key={ev.id} className="card-surface rounded-2xl p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{ev.title}</p>
                    <p className="text-xs text-[#A1A1A1]">
                      {new Date(ev.start_time).toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric', month: 'short' })}
                      {' · '}
                      {new Date(ev.start_time).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}–{new Date(ev.end_time).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                      {ev.location ? ` · ${ev.location}` : ''}
                    </p>
                  </div>
                  <span className="label-text text-[0.6rem] text-[#A1A1A1] shrink-0">Google</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {past.length > 0 && (
          <>
            <h2 className="heading-text text-2xl text-white mb-6">Historia</h2>
            <div className="space-y-3">
              {past.map(session => (
                <SessionRow key={session.id} session={session} busy={busyId === session.id} onDelete={() => handleDelete(session)} historical />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

const SessionRow = ({ session, busy, onComplete, onNoShow, onCancel, onDelete, historical }) => {
  const meta = STATUS_META[session.status] || STATUS_META.scheduled;
  const start = new Date(session.start_time);
  const end = new Date(session.end_time);
  return (
    <div className="card-surface rounded-3xl p-6 flex items-center justify-between flex-wrap gap-4" data-testid={`session-row-${session.id}`}>
      <div>
        <div className="flex items-center gap-3 mb-1">
          <h3 className="heading-text text-lg text-white">{session.client_name}</h3>
          <span className={`text-xs font-medium px-3 py-1 rounded-full border ${meta.className}`}>{meta.label}</span>
          {session.package_label && (
            <span className="text-xs text-[#A1A1A1] px-3 py-1 rounded-full border border-[#2A2A2A]">{session.package_label}</span>
          )}
          {sessionUnits(session) > 1 && (
            <span className="text-xs text-[#C9A84C] px-3 py-1 rounded-full border border-[#C9A84C]/40">{unitsLabel(sessionUnits(session))}</span>
          )}
        </div>
        <p className="text-sm text-[#A1A1A1]">
          {start.toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric', month: 'short' })} • {start.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}–{end.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
          {session.location && ` • ${session.location}`}
        </p>
        {session.notes && <p className="text-sm text-[#A1A1A1] mt-1 italic">{session.notes}</p>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!historical && session.status === 'scheduled' && (
          <>
            <Button onClick={onComplete} disabled={busy} variant="ghost" size="sm" className="text-green-500 hover:text-white" title="Oznacz jako odbytą" data-testid={`complete-session-${session.id}`}>
              <Check className="w-4 h-4" />
            </Button>
            <Button onClick={onNoShow} disabled={busy} variant="ghost" size="sm" className="text-[#A1A1A1] hover:text-white" title="Nieobecność klienta">
              NC
            </Button>
            <Button onClick={onCancel} disabled={busy} variant="ghost" size="sm" className="text-red-400 hover:text-white" title="Anuluj" data-testid={`cancel-session-${session.id}`}>
              <X className="w-4 h-4" />
            </Button>
          </>
        )}
        <Button onClick={onDelete} disabled={busy} variant="ghost" size="sm" className="text-[#A1A1A1] hover:text-red-400" title="Usuń">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
